import React, { useEffect, useState } from "react";
import { OffCanvas } from "~assets/frontend/components/comp-offcanvas/comp-offcanvas";
import Translator from "bazinga-translator";
import { PrimaryButton } from "~assets/frontend/elements/button/Buttons";
import { createPortal } from "react-dom";
import { Checkbox } from "~assets/frontend/elements/checkbox/Checkbox";
import { FormGroup } from "~assets/frontend/elements/form-group/FormGroup";
import { RangeSlider } from "~assets/frontend/components/comp-range_slider/comp-range_slider";
import { AUTOMATIC, format_number } from "~assets/frontend/_util/format_number";
import { TextButton } from "~assets/frontend/elements/text-button/TextButton";
export const InteractiveTableFilter = (props) => {
    const [showFilter, setShowFilter] = useState(false);
    const [productIds, setProductIds] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(new Map()); // Map<value, filterName>
    useEffect(() => {
        const availableProducts = new Set();
        [...selectedFilter.entries()].forEach(([filterName, values]) => {
            const filterConfig = props.data.get(filterName);
            let rowIds = [];
            if (filterConfig === undefined) {
                return;
            }
            // the values from a range slider are min / max not the set of selected values
            if (canRenderRangeSlider(filterConfig) && values.length === 2) {
                const min = values[0];
                const max = values[1];
                filterConfig.valueMap.forEach((tableInfo, value) => {
                    if (parseFloat(`${min}`) <= parseFloat(`${value}`) && parseFloat(`${max}`) >= parseFloat(`${value}`)) {
                        rowIds = [...rowIds, ...tableInfo.rowIds];
                    }
                });
            }
            else {
                values.forEach((value) => {
                    var _a;
                    const newRowIds = ((_a = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.valueMap.get(value)) === null || _a === void 0 ? void 0 : _a.rowIds) || [];
                    rowIds = [...rowIds, ...newRowIds];
                });
            }
            const uniqueRowIds = new Set([...rowIds]);
            if (availableProducts.size === 0) {
                for (const id of uniqueRowIds) {
                    availableProducts.add(id);
                }
            }
            else {
                for (const id of availableProducts) {
                    if (!uniqueRowIds.has(id)) {
                        availableProducts.delete(id);
                    }
                }
            }
        });
        setProductIds([...availableProducts]);
    }, [selectedFilter]);
    const reset = () => {
        setSelectedFilter(new Map());
    };
    const getCheckboxValue = (filterName, value) => {
        const filterValues = selectedFilter.get(filterName);
        if (filterValues) {
            return filterValues.findIndex((selected) => selected === value) !== -1;
        }
        return false;
    };
    const getRangeValue = (filterName) => {
        const values = selectedFilter.get(filterName);
        if (values === undefined) {
            return { min: undefined, max: undefined };
        }
        const numbers = values.filter((value) => typeof value === "number");
        if (numbers.length !== 2) {
            return { min: undefined, max: undefined };
        }
        return {
            min: numbers[0],
            max: numbers[1],
        };
    };
    const canRenderRangeSlider = (filterConfig) => {
        if ([...filterConfig.valueMap.keys()].some((value) => typeof value !== "number")) {
            return false;
        }
        return filterConfig.valueMap.size >= 5;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flex items-center justify-between gap-24" },
            selectedFilter.size > 0 && (React.createElement(TextButton, { label: Translator.trans("product_group.page.table.filter.discard", {}, "frontend"), icon: { identifier: "icon-back", position: "start" }, onClick: () => {
                    reset();
                    props.onUpdate([]);
                } })),
            React.createElement(TextButton, { label: Translator.trans("product_group.page.table.filter.headline"), onClick: () => {
                    setShowFilter(true);
                }, icon: { identifier: "icon-sliders", position: "start" } })),
        createPortal(React.createElement(OffCanvas, { showCanvas: showFilter, onClose: () => {
                if (!(productIds.length === 0 && selectedFilter.size > 0)) {
                    setShowFilter(false);
                    props.onUpdate(productIds);
                }
            }, headline: Translator.trans("product_group.page.table.filter.headline", {}, "frontend"), content: React.createElement("div", null, [...props.data.entries()]
                .filter(([filterName, filterConfig]) => {
                return filterConfig.isFilterable && filterConfig.isShown && filterConfig.valueMap.size > 1;
            })
                .map(([filterName, filterConfig]) => {
                if (canRenderRangeSlider(filterConfig)) {
                    const values = [...filterConfig.valueMap.keys()].filter((value) => {
                        return typeof value === "number";
                    });
                    return (React.createElement(RangeSlider, { key: filterName, label: filterConfig.title, min: Math.min(...values), max: Math.max(...values), valueMin: getRangeValue(filterName).min, valueMax: getRangeValue(filterName).max, unit: filterConfig.unit || undefined, step: 0.1, digits: 1, onChange: (min, max) => {
                            const newSelectedFilter = new Map([...selectedFilter]);
                            if (parseFloat(min) === Math.min(...values) && parseFloat(max) === Math.max(...values)) {
                                newSelectedFilter.delete(filterName);
                            }
                            else {
                                newSelectedFilter.set(filterName, [parseFloat(min), parseFloat(max)]);
                            }
                            setSelectedFilter(newSelectedFilter);
                        } }));
                }
                return (React.createElement(FormGroup, { label: filterConfig.title, key: filterName }, [...filterConfig.valueMap].map(([value, columnInfo]) => {
                    let image;
                    if (columnInfo.imageValue) {
                        image = {
                            _id: `${value}`,
                            _name: "ImageStruct",
                            fieldValue: columnInfo.imageValue,
                            caption: null,
                            mask: null,
                            copyright: null,
                        };
                        if (value === "proClean") {
                            image = "/build/images/producttable/proClean.svg";
                        }
                        else if (value === "proPower") {
                            image = "/build/images/producttable/proPower.svg";
                        }
                    }
                    let label = value;
                    if (typeof label === "number") {
                        label = format_number(value, AUTOMATIC, { minimumFractionDigits: 1 });
                    }
                    if (filterConfig.unit !== null) {
                        label = `${label} ${filterConfig.unit}`;
                    }
                    return (React.createElement(Checkbox, { key: value, label: label, image: image, value: getCheckboxValue(filterName, value), onChange: (checked) => {
                            const newSelectedFilter = new Map([...selectedFilter]);
                            if (checked) {
                                const selectedCheckboxes = newSelectedFilter.get(filterName) || [];
                                if (selectedCheckboxes.findIndex((selected) => selected === value) === -1) {
                                    newSelectedFilter.set(filterName, [...selectedCheckboxes, value]);
                                }
                            }
                            else {
                                const selectedCheckboxes = newSelectedFilter.get(filterName);
                                if (selectedCheckboxes) {
                                    const index = selectedCheckboxes.findIndex((selected) => selected === value);
                                    if (index !== -1) {
                                        selectedCheckboxes.splice(index, 1);
                                        if (selectedCheckboxes.length === 0) {
                                            newSelectedFilter.delete(filterName);
                                        }
                                        else {
                                            newSelectedFilter.set(filterName, selectedCheckboxes);
                                        }
                                    }
                                }
                            }
                            setSelectedFilter(newSelectedFilter);
                        } }));
                })));
            })), actions: React.createElement("div", { className: "flex flex-row flex-nowrap items-center gap-12 md:gap-40" },
                React.createElement(PrimaryButton, { label: Translator.trans("product_group.page.table.filter.save", {}, "frontend"), disabled: productIds.length === 0 && selectedFilter.size > 0, onClick: () => {
                        props.onUpdate(productIds);
                        setShowFilter(false);
                    } }),
                React.createElement(TextButton, { label: Translator.trans("product_group.page.table.filter.discard", {}, "frontend"), disabled: selectedFilter.size === 0, onClick: reset, icon: { identifier: "icon-close", position: "start" } })) }), document.body, props.id + "_offcanvas")));
};
